import { CustomFieldDefinitionActionAvailability } from '@/api/queries/customField/useGetFieldset';
import { FormValues } from '@/ui/components/customFieldGroupControl/CreateEditCustomFieldSidebar';
import { Button, Card, EmptyState, Field } from '@payaca/ambrosia';

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { appendPath, FieldsetFieldError, PathToFieldsetChild } from '../types';
import FieldsetChildrenList from './FieldsetChildrenList';

export type TNestedFieldsetChildrenEditorProps = {
  parentFieldName: PathToFieldsetChild;
  isPersisted: boolean;
  actionAvailability?: CustomFieldDefinitionActionAvailability[];
  fieldsetIdentifier?: string;
  parentFieldIdentifier: string;
  onOpenNestedFieldEditor?: (parentIndex: number, nestedIndex: number) => void;
  isNestedFieldEditorOpen?: boolean;
};

const NestedFieldsetChildrenEditor: FC<TNestedFieldsetChildrenEditorProps> = (
  props
) => {
  const {
    parentFieldName,
    isPersisted,
    actionAvailability,
    fieldsetIdentifier,
    parentFieldIdentifier,
    onOpenNestedFieldEditor,
    isNestedFieldEditorOpen,
  } = props;

  // Extract parent index from parentFieldName (e.g., "children.2" -> 2)
  const parentIndex = parseInt(parentFieldName.split('.')[1], 10);

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  // Track which field is visually selected in the list (for highlighting)
  const [selectedNestedFieldId, setSelectedNestedFieldId] = useState<
    string | undefined
  >();

  // Track pending auto-open index to avoid stale closure issues
  const pendingAutoOpenIndexRef = useRef<number | null>(null);

  const childrenFieldName = appendPath(
    // The rest of the implementation is depth agnostic, but since we are
    // ultimately validating against a Zod schema with a finite depth, we
    // have to narrow the type to the 2nd deepest supported level here, i.e.
    // one level short of the maximum supported depth.
    parentFieldName as `children.${number}`,
    'children'
  );

  const { fields, append, remove, move } = useFieldArray({
    name: childrenFieldName,
    control,
  });

  const fieldsetChildren = watch(childrenFieldName) || [];
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...fieldsetChildren[index],
  }));

  // Parent must be a fieldset (only fieldsets have child fields)
  const parentFieldError = errors.children?.[parentIndex] as
    | FieldsetFieldError
    | undefined;
  const nestedChildrenErrors = parentFieldError?.children;

  // Auto-open the editor for newly added fields
  useEffect(() => {
    const pendingIndex = pendingAutoOpenIndexRef.current;
    if (pendingIndex !== null && controlledFields[pendingIndex]) {
      setSelectedNestedFieldId(controlledFields[pendingIndex].id);
      onOpenNestedFieldEditor?.(parentIndex, pendingIndex);
      pendingAutoOpenIndexRef.current = null;
    }
  }, [controlledFields, onOpenNestedFieldEditor, parentIndex]);

  // Clear selection when the sidebar closes, so clicking the same field again will re-open it
  useEffect(() => {
    if (!isNestedFieldEditorOpen) {
      setSelectedNestedFieldId(undefined);
    }
  }, [isNestedFieldEditorOpen]);

  const handleSelectField = useCallback(
    (selectedId: string) => {
      setSelectedNestedFieldId(selectedId);
      const nestedIndex = controlledFields.findIndex(
        (f) => f.id === selectedId
      );
      if (nestedIndex >= 0) {
        onOpenNestedFieldEditor?.(parentIndex, nestedIndex);
      }
    },
    [controlledFields, onOpenNestedFieldEditor, parentIndex]
  );

  const handleRemoveField = useCallback(
    (index: number) => {
      const removedField = controlledFields[index];
      remove(index);
      if (selectedNestedFieldId === removedField?.id) {
        setSelectedNestedFieldId(undefined);
      }
    },
    [controlledFields, remove, selectedNestedFieldId]
  );

  return (
    <>
      <Card variant="default" className="mt-4">
        <Card.Body>
          <Field name={childrenFieldName}>
            <div className="flex justify-between mb-4">
              <Field.Label className="m-0">Nested fields</Field.Label>

              <Button
                className="-mt-2"
                variant="white"
                size="sm"
                onClick={() => {
                  // Track the index for auto-opening editor after the component re-renders
                  pendingAutoOpenIndexRef.current = fields.length;
                  append({
                    type: 'text',
                    identification: {
                      label: '',
                      identifier: '',
                    },
                  });
                }}
              >
                Add field
              </Button>
            </div>

            <FieldsetChildrenList
              fields={controlledFields}
              selectedId={selectedNestedFieldId}
              onSelect={handleSelectField}
              onRemove={handleRemoveField}
              onMove={move}
              canRemoveChild={(index) => {
                const field = controlledFields[index];
                const fieldPath = `${fieldsetIdentifier}.${parentFieldIdentifier}.${field.identification?.identifier}`;
                const aa = actionAvailability?.find(
                  (x) => x.fieldPath === fieldPath
                );
                const isDestructivelyUpdateable =
                  !aa || aa.isDestructivelyUpdateable.result;
                return !isPersisted || isDestructivelyUpdateable;
              }}
              hasError={(index) => !!nestedChildrenErrors?.[index]}
              emptyStateText="Add fields to this nested fieldset"
            />
          </Field>
        </Card.Body>
      </Card>

      {typeof selectedNestedFieldId !== 'string' &&
        controlledFields.length > 0 && (
          <EmptyState
            className="mt-4"
            iconName="cursor-box.3"
            text="Select a field to edit"
          />
        )}
    </>
  );
};

export default NestedFieldsetChildrenEditor;
